import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { LegalEntityQueryField, LegalEntityStatus, LegalEntityType } from 'chaika-enums';
import { LegalEntityData, LegalEntityListQuery } from 'chaika-frontend-types/frontend-cms';

import { OptionType } from '@components/Select';
import { collectLegalEntitiesByIdsFromCache, legalEntityQueryOptions } from '@organization/queries/legalEntityQueries';
import { makeOptionType } from '@utils/makeOptionTypes';

// LegalEntityListQuery wraps the backend type via StringArrayAsString, so array
// filters cross the wire as comma-joined strings. Sorting keeps the query key
// stable when callers pass the same values in different orders.
function toJoinedFilter<T extends string>(values: T[]): string | undefined {
    return values.length > 0 ? values.slice().sort().join() : undefined;
}

interface UseLegalEntityOptionsParams {
    types: LegalEntityType[];
    statuses: LegalEntityStatus[];
    searchTypes: LegalEntityType[];
    searchStatuses: LegalEntityStatus[];
    selectedIds: string[];
}

interface Data {
    defaultOptions: OptionType<string>[];
    loadOptions(query: string): Promise<OptionType<string>[]>;
    isLoading: boolean;
}

export function useLegalEntityOptions({
    types,
    statuses,
    searchTypes,
    searchStatuses,
    selectedIds,
}: UseLegalEntityOptionsParams): Data {
    const typeKey = toJoinedFilter(types);
    const statusKey = toJoinedFilter(statuses);
    const searchTypeKey = toJoinedFilter(searchTypes);
    const searchStatusKey = toJoinedFilter(searchStatuses);

    const queryClient = useQueryClient();

    const { data: scoped = [], isLoading: scopedLoading } = useQuery(
        legalEntityQueryOptions.list({
            ...(typeKey ? { type: typeKey } : {}),
            ...(statusKey ? { status: statusKey } : {}),
        }),
    );
    const { data: selected = [], isLoading: selectedLoading } = useQuery({
        ...legalEntityQueryOptions.byIds(selectedIds),
        initialData: () => collectLegalEntitiesByIdsFromCache(queryClient, selectedIds),
    });

    // Scoped first (preserving server order), then any saved IDs that fall
    // outside the scope (archived / cross-type) appended so their names render.
    const defaultOptions = useMemo(() => {
        if (selected.length === 0) return makeOptionType(scoped);
        const byId = new Map<string, LegalEntityData>();
        scoped.forEach(le => byId.set(le.id, le));
        selected.forEach(le => {
            if (!byId.has(le.id)) byId.set(le.id, le);
        });
        return makeOptionType([...byId.values()]);
    }, [scoped, selected]);

    const debouncedFetch = useMemo(
        () =>
            AwesomeDebouncePromise(
                (filters: LegalEntityListQuery) => queryClient.fetchQuery(legalEntityQueryOptions.list(filters)),
                300,
            ),
        [queryClient],
    );

    const loadOptions = useCallback(
        async (query: string): Promise<OptionType<string>[]> => {
            const trimmed = query.trim();
            if (!trimmed) return [];
            const matches = await debouncedFetch({
                query: trimmed,
                queryFields: LegalEntityQueryField.Name,
                ...(searchTypeKey ? { type: searchTypeKey } : {}),
                ...(searchStatusKey ? { status: searchStatusKey } : {}),
            });
            return makeOptionType(matches);
        },
        [debouncedFetch, searchTypeKey, searchStatusKey],
    );

    return { defaultOptions, loadOptions, isLoading: scopedLoading || selectedLoading };
}
